/**
 * Name: Edit Playlist
 * Author: @Gaurav
 * Date: 20-07-2021
 */
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import Checkbox from '@material-ui/core/Checkbox';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Grid from '@material-ui/core/Grid';
import { set, size } from 'lodash';
import Paper from '@material-ui/core/Paper';
import { CircularProgress } from '@material-ui/core';

import { playlistStore, songsStore } from '../../store';
import { playlistActions, songActions } from '../../actions';
import useClickOutside from '../../utils/useClickOutside';
import SongPlayer from '../../components/SongPlayer';
import PlayBtn from '../../assets/play_btn.svg';
import { tooltipClasses } from '@mui/material';

/* 
 Table Data and Use Style
 */

function EditPlaylist({ searchInput }) {
  const LIMIT = 50;
  const location = useLocation();

  const [allPlaylists, setAllPlaylists] = useState([]);
  const [myPlaylist, setMyPlaylist] = useState([]);
  const [songsFromSelectedPlaylist, setSongsFromSelectedPlaylist] = useState([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState({ page: 1, isLast: false });
  const [checked, setChecked] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const currentPlaylist = location?.state?.playlistDetails;

  // zustand Playlist store
  const { selectedPlaylist, addSelectedPlaylist } = playlistStore((state) => {
    return {
      selectedPlaylist: state.selectedPlaylist,
      setSelectedPlaylist: state.setSelectedPlaylist,
      addSelectedPlaylist: state.addSelectedPlaylist,
      removeSelectedPlaylist: state.removeSelectedPlaylist,
    };
  });

  // Zustand Songs Store
  const { addSongList, songs, addSingleSong, removeSingleSong, setSongs } = songsStore((state) => {
    return {
      setCurrSongToPlay: state.setCurrSongToPlay,
      currentSong: state.currSongToPlay,
      songs: state.songs,
      addSongList: state.addSongList,
      addSingleSong: state.addSong,
      removeSingleSong: state.removeSong,
      setSongs: state.setSongs,
    };
  });

  const filterAllOtherThanSelected = (playList) => {
    if (Array.isArray(playList) && playList.length) {
      return playList.filter((item) => item.id !== currentPlaylist?.id);
    }
    return [];
  };

  const handleSelectPlaylist = (event, playList) => {
    if (event.target.checked) {
      // Check if playlist is already in zustand selected playlist store if not then fetch the api or else get it from zustand store
      if (!selectedPlaylist.find((item) => item.id === playList.id)) {
        // Fetch playlist by ID
        const fetchPlaylistById = async () => {
          try {
            const fetchPlaylistByIdResponse = await playlistActions.fetchPlaylistById(playList.id);
            if (fetchPlaylistByIdResponse) {
              addSelectedPlaylist(fetchPlaylistByIdResponse?.data);
              setMyPlaylist([...myPlaylist, fetchPlaylistByIdResponse?.data]);
            }
          } catch (error) {
            console.log('error', error);
          }
        };

        fetchPlaylistById();
      } else {
        setMyPlaylist([...myPlaylist, selectedPlaylist.find((item) => item.id === playList.id)]);
      }
    } else {
      setMyPlaylist(myPlaylist.filter((item) => item.id !== playList.id));
      // removeSelectedPlaylist(playList);
    }
  };

  const handleSingleSongCheck = (event, song) => {
    if (songs.some((item) => item.id === song.id)) {
      removeSingleSong(song);
      setChecked(false);
    } else {
      addSingleSong(song);
      setChecked(true);
    }
  };

  const handleAllSongsCheck = (event) => {
    if (event.target.checked) {
      const mergedSongs = [...songsFromSelectedPlaylist, ...songs];
      addSongList(mergedSongs);
    } else {
      setSongs([]);
    }
  };

  const fetchMorePlaylist = async () => {
    if (isFetchingMore || currentPage.isLast) return;

    setIsFetchingMore(true);
    const PlaylistsFromBE = await playlistActions.fetchAllPlaylist({
      page: 1,
      limit: LIMIT,
      type: location.state.playlistDetails.type,
    });
    setAllPlaylists((prev) => [...prev, ...PlaylistsFromBE]);
    setIsFetchingMore(false);
    setCurrentPage((prev) => ({
      page: prev.page + 1,
      isLast: size(PlaylistsFromBE) < LIMIT,
    }));
  };

  const [isSongDialogOpen, setIsSongDialogOpen] = useState({
    id: 0,
    isOpen: false,
  });
  const [songDetail, setSongDetail] = useState({});

  const toolTipRefs = useRef(null);

  const onPlayClick = (song, index) => {
    setSongDetail({
      song_name: song.title || '',
      artist_name: song.artist_name || '',
      songgenre: song.genre || [],
      description: song.description || '',
      thumbnail_img: song.thumbnail_img || '',
      license: song.license_name || '',
      termsandconditions: song.tc_sign || '',
      created_by: song.created_by || '',
      creator_id: song.creator_id || '',
      plan: song.plan || [],
      audio_track: song.audio_track || '',
    });

    setIsSongDialogOpen({
      id: index,
      isOpen: true,
    });
  };

  useEffect(() => {
    const MergeAllSongs = myPlaylist.map((item) => item.songs).flat();
    const uniqueSong = MergeAllSongs.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i);
    setSongsFromSelectedPlaylist(uniqueSong);
    toolTipRefs.current = uniqueSong.map(() => React.createRef());
  }, [myPlaylist]);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && !currentPage.isLast) {
      fetchMorePlaylist();
    }
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const PlaylistsFromBE = await playlistActions.fetchAllPlaylist({
        page: 1,
        limit: LIMIT,
        type: location.state.playlistDetails.type,
        search: searchInput,
      });

      setAllPlaylists(PlaylistsFromBE);
      setIsLoading(false);
      setCurrentPage((prev) => ({
        page: prev.page + 1,
        isLast: size(PlaylistsFromBE) < LIMIT,
      }));
    })();
  }, [searchInput]);

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 30,
        border: '1px solid #40189d',
      }}
      className="container table"
    >
      <TableContainer
        className="tbl_wraper tbl_wrap1 edit-playlist-table table-container"
        style={{ margin: 0, borderRight: '1px solid #40189d' }}
        component={Paper}
        onScroll={handleScroll}
      >
        <Table style={{ borderTopRightRadius: 0 }} stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell className="checkbox">
                <Checkbox disabled inputProps={{ 'aria-label': 'uncontrolled-checkbox' }} />
              </TableCell>
              <TableCell className="result">Result</TableCell>
              <TableCell>Playlist Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <div className="table_initial_loader">
                <CircularProgress />
              </div>
            )}
            {filterAllOtherThanSelected(allPlaylists).length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center' }}>
                  No songs found
                </TableCell>
              </TableRow>
            ) : (
              <>
                {filterAllOtherThanSelected(allPlaylists).map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className="checkbox">
                      <Checkbox
                        checked={myPlaylist.some((item) => item.id === row.id) || false}
                        onChange={(event) => handleSelectPlaylist(event, row)}
                        inputProps={{ 'aria-label': 'uncontrolled-checkbox' }}
                      />
                    </TableCell>
                    <TableCell className="result">#{++index}</TableCell>
                    <TableCell className="table-name-cell">{row.name}</TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {isFetchingMore && (
              <TableRow className="fetch_more_loader">
                <CircularProgress />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer className="tbl_wraper tbl_wrap2 edit-playlist-table" style={{ margin: 0 }} component={Paper}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell className="checkbox">
                <Checkbox inputProps={{ 'aria-label': 'uncontrolled-checkbox' }} onChange={handleAllSongsCheck} />
              </TableCell>
              <TableCell className="result">Result</TableCell>
              <TableCell style={{ minWidth: 150 }}>Song Name</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {songsFromSelectedPlaylist?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center' }}>
                  No songs found
                </TableCell>
              </TableRow>
            ) : (
              <>
                {songsFromSelectedPlaylist?.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className="checkbox">
                      <Checkbox
                        onChange={(event) => handleSingleSongCheck(event, row)}
                        inputProps={{ 'aria-label': 'uncontrolled-checkbox' }}
                        name="song"
                        checked={songs.some((song) => song.id === row.id)}
                      />
                    </TableCell>
                    <TableCell className="result">#{++index}</TableCell>
                    <TableCell className="table-name-cell">{row.title || 'N/A'}</TableCell>
                    <TableCell className="songPlayerContainer" style={{ position: 'relative' }}>
                      <Grid item ref={toolTipRefs?.current?.[index]} className="tooltip-wrapper">
                        <img
                          src={PlayBtn}
                          onClick={() => {
                            onPlayClick(row, index);
                          }}
                          alt=""
                        />
                        {isSongDialogOpen.isOpen && isSongDialogOpen.id === index && (
                          <SongPlayer
                            songDetail={songDetail}
                            onClose={() => {
                              setIsSongDialogOpen({
                                id: 0,
                                isOpen: false,
                              });
                            }}
                          />
                        )}
                      </Grid>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
export default EditPlaylist;

